import { ApiResponse } from '../http/BaseHttpClient';
import { IProduct } from '../types';
import AbstractService from './AbstractService';

export class PostService extends AbstractService {
  async getPaginatedPosts(params?: Record<string, any>): Promise<Record<string, any>> {
    const response = await this.http.request<ApiResponse<IProduct[]>>({
      method: 'get',
      url: this.endpoint('faqPosts'),
      params,
    });
    const raw = (response as any)?.data;

    // Normalize array-only responses to { data: [...] } while preserving ApiResponse shape.
    const normalized = Array.isArray(raw) ? ({ data: raw } as unknown as ApiResponse<IProduct[]>) : (raw as ApiResponse<IProduct[]>);

    const payload = normalized;

    if ((payload as any)?.error) {
      throw new Error((payload as any).error?.message || 'Failed to fetch posts');
    }

    return payload;
  }

  async getPosts(params?: Record<string, any>): Promise<Record<string, any>> {
    const response = await this.get<Record<string, any>>(this.endpoint('faqPosts'), params);
    const data = (response as any)?.data ?? response;

    if ((data as any)?.error) {
      throw new Error((data as any).error?.message || 'Failed to fetch posts');
    }
    return data;
  }

  async getPostBySlug(slug: string): Promise<Record<string, any>> {
    const response = await this.get<Record<string, any>>(this.endpoint('faqPostBySlug', { slug }));
    const data = (response as any)?.data ?? response;

    if ((data as any)?.error) {
      throw new Error((data as any).error?.message || 'Failed to fetch post');
    }
    return data;
  }

  async getPostById(id: string | number): Promise<Record<string, any>> {
    const response = await this.get<Record<string, any>>(this.endpoint('postById', { id }));
    const data = (response as any)?.data ?? response;

    if ((data as any)?.error) {
      throw new Error((data as any).error?.message || 'Failed to fetch post');
    }
    return data;
  }

  async updatePost(id: string | number, data: Record<string, any>): Promise<Record<string, any>> {
    const response = await this.patch<Record<string, any>>(this.endpoint('postById', { id }), data);
    const responseData = (response as any)?.data ?? response;

    if ((responseData as any)?.error) {
      throw new Error((responseData as any).error?.message || 'Failed to update post');
    }
    return responseData;
  }

  async incPostView(id: number | string): Promise<Record<string, any>> {
    const post = await this.getPostById(id);
    if ((post as any)?.error) {
      throw new Error((post as any).error?.message || 'Failed to fetch post for view increment');
    }
    const previous_views_count = (post as any)?.views_count || 0;
    const payload = { views_count: previous_views_count + 1 };
    const response = await this.updatePost(id, payload);
    return response;
  }
}

export const postService = new PostService();

export default PostService;
